import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeroVideo } from '../components/HeroVideo';
import { ServiceVideoCard } from '../components/ServiceVideoCard';
import { CallNowButton } from '../components/CallNowButton';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { ASSETS, SERVICES, COMPANY_INFO } from '../config/assets';
import { ArrowRight, Building, Home, Sparkles } from 'lucide-react';

const HomePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.35 }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <HeroVideo
        videoSrc={ASSETS.videos.hero}
        title="Polished Concrete, Perfected in Sydney"
        subtitle="Transform your commercial or residential space with premium polished concrete solutions. Sustainable. Durable. Beautiful."
        primaryCTA={{ text: 'Get a Quote', link: '/contact' }}
        secondaryCTA={{ text: 'Our Services', link: '/services' }}
      />

      {/* Services Section */}
      <section className="section-spacing bg-background" data-testid="services-section">
        <div className="container-custom">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-testid="services-heading">
              Our Premium Services
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Specialized concrete solutions for every need. From high-gloss polishing to professional surface prep.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="services-grid">
            {SERVICES.map((service, index) => (
              <motion.div
                key={service.slug}
                {...fadeInUp}
                transition={{ duration: 0.35, delay: index * 0.1 }}
              >
                <ServiceVideoCard
                  video={service.video}
                  title={service.name}
                  description={service.description}
                  testid={`service-card-${service.slug}`}
                />
              </motion.div>
            ))}
          </div>

          <motion.div className="text-center mt-12" {...fadeInUp}>
            <Link to="/services">
              <Button className="btn-primary" size="lg" data-testid="view-all-services-btn">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Commercial vs Residential */}
      <section className="section-spacing bg-secondary/20" data-testid="commercial-residential-section">
        <div className="container-custom">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Tailored for Your Space
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Expert solutions for both commercial and residential projects
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div {...fadeInUp}>
              <Card className="relative overflow-hidden h-[400px] group hover:shadow-xl transition-shadow duration-300" data-testid="commercial-card">
                <img
                  src="https://customer-assets.emergentagent.com/job_concrete-vision/artifacts/pk5bpnpo_20250313_160641.jpg"
                  alt="Commercial Space"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <CardContent className="relative h-full flex flex-col justify-end p-8 text-white">
                  <Building className="h-12 w-12 mb-4 text-primary" />
                  <h3 className="text-2xl font-bold mb-3">Commercial Spaces</h3>
                  <p className="text-white/90 mb-6">
                    Offices, retail stores, warehouses, and industrial facilities. High-traffic, low-maintenance solutions.
                  </p>
                  <Link to="/commercial-residential">
                    <Button variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="commercial-learn-more-btn">
                      Learn More
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ duration: 0.35, delay: 0.1 }}>
              <Card className="relative overflow-hidden h-[400px] group hover:shadow-xl transition-shadow duration-300" data-testid="residential-card">
                <img
                  src="https://customer-assets.emergentagent.com/job_concrete-vision/artifacts/8tyf58fm_20240213_111427.jpg"
                  alt="Residential Space"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <CardContent className="relative h-full flex flex-col justify-end p-8 text-white">
                  <Home className="h-12 w-12 mb-4 text-primary" />
                  <h3 className="text-2xl font-bold mb-3">Residential Spaces</h3>
                  <p className="text-white/90 mb-6">
                    Villas, apartments, and luxury homes. Elegant, sustainable flooring that enhances your living space.
                  </p>
                  <Link to="/commercial-residential">
                    <Button variant="outline" className="border-white/40 text-white hover:bg-white/10" data-testid="residential-learn-more-btn">
                      Learn More
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Portfolio */}
      <section className="section-spacing bg-background" data-testid="featured-portfolio-section">
        <div className="container-custom">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Featured Project: Doltone House
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Stunning polished concrete for an elegant wedding reception venue
            </p>
          </motion.div>

          <motion.div {...fadeInUp}>
            <Card className="overflow-hidden" data-testid="doltone-house-preview">
              <div className="grid md:grid-cols-2">
                <div className="relative h-[400px] md:h-auto">
                  <img
                    src={ASSETS.images.doltoneHouse[0]}
                    alt="Doltone House Reception Venue"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-8 md:p-12 flex flex-col justify-center">
                  <Sparkles className="h-10 w-10 text-primary mb-4" />
                  <h3 className="text-2xl font-bold mb-4">Doltone House Reception Venue</h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    A showcase of our premium polished concrete work for one of Sydney's most prestigious wedding venues. 
                    Featuring high-gloss aggregate exposure and exceptional durability for high-traffic events.
                  </p>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                      <p className="text-2xl font-bold text-primary">1,000+</p>
                      <p className="text-sm text-muted-foreground">SQM Polished</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-primary">800</p>
                      <p className="text-sm text-muted-foreground">Grit Polish</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-primary">Full Aggregate</p>
                      <p className="text-sm text-muted-foreground">Exposure</p>
                    </div>
                  </div>
                  <Link to="/portfolio">
                    <Button className="btn-primary" data-testid="view-portfolio-btn">
                      View Full Portfolio
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                </CardContent>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="section-spacing bg-secondary/20" data-testid="blog-section">
        <div className="container-custom">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Latest from Our Blog
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Expert tips, project insights, and industry knowledge
            </p>
          </motion.div>

          <motion.div {...fadeInUp}>
            <Carousel className="w-full" data-testid="blog-carousel">
              <CarouselContent>
                {[
                  {
                    title: 'Concrete Grinding and Sealing: Durable, Stylish Floors Made Simple',
                    excerpt: 'If you\'re looking for a tough, low-maintenance flooring solution that also looks great, concrete grinding and sealing might be exactly what you need...',
                    image: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/09/WhatsApp-Image-2025-09-02-at-18.48.441-1024x768.jpeg',
                    category: 'Commercial',
                    date: 'September 5, 2025'
                  },
                  {
                    title: 'Sydney Concrete Polishing: What to Expect from Start to Finish',
                    excerpt: 'There\'s growing demand for Sydney concrete polishing among those renovating homes or commercial spaces...',
                    image: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20210927_125521-scaled.jpg',
                    category: 'Education',
                    date: 'August 26, 2025'
                  },
                  {
                    title: 'Polished Concrete Flooring Sydney – Top 7 Benefits for Homeowners and Businesses',
                    excerpt: 'Polished concrete floors Sydney property owners love this modern flooring solution as it is becoming one of the most practical and attractive flooring finishes...',
                    image: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20220419_154021-scaled.jpg',
                    category: 'Residential',
                    date: 'August 18, 2025'
                  },
                  {
                    title: 'Sydney Polished Concrete Specialists- Quality Flooring for Modern Spaces',
                    excerpt: 'Polished concrete floors in Sydney homes are becoming the go-to solution for modern-looking spaces, and the service is great for practical living...',
                    image: 'https://sydneyconcretepolishers.com.au/wp-content/uploads/2025/03/20211213_225634-scaled.jpg',
                    category: 'Commercial',
                    date: 'August 11, 2025'
                  }
                ].map((post, index) => (
                  <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                    <Card className="overflow-hidden h-full hover:shadow-xl transition-shadow duration-300">
                      <div className="aspect-video relative overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold">
                            {post.category}
                          </span>
                        </div>
                      </div>
                      <CardContent className="pt-6">
                        <p className="text-sm text-muted-foreground mb-2">{post.date}</p>
                        <h3 className="text-lg font-bold mb-3 line-clamp-2">{post.title}</h3>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                        <Link to="/blog">
                          <Button variant="ghost" className="p-0 h-auto" data-testid={`blog-read-more-${index}`}>
                            Read More
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-4" />
              <CarouselNext className="right-4" />
            </Carousel>
          </motion.div>

          <motion.div className="text-center mt-8" {...fadeInUp}>
            <Link to="/blog">
              <Button className="btn-primary" data-testid="view-all-blog-btn">
                View All Articles
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-[radial-gradient(1200px_600px_at_50%_50%,#EEEDEC_0%,#F7F7F6_45%,#FAFAF9_100%)]" data-testid="cta-section">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Transform Your Space?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Get a free consultation and quote for your concrete polishing project in Sydney
            </p>
            <Link to="/contact">
              <Button className="btn-primary" size="lg" data-testid="cta-contact-btn">
                Get Your Free Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* Call Now Button - Homepage Only */}
      <CallNowButton />
    </div>
  );
};

export default HomePage;
